import { Component, OnInit, ViewChild, ChangeDetectorRef, SecurityContext, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { PagedResult, PagedResultV2 } from 'src/app/shared/models/paging';
import { Address } from 'src/app/shared/models/address';
import { BehaviorSubject, debounceTime, distinctUntilChanged, firstValueFrom, startWith, tap } from 'rxjs';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ChargingStationLocation,
  ChargingStationLocationSearchEvent,
} from 'src/app/features/charging-station-location/charging-station-location-model';
import { Department, DepartmentSearchEvent } from 'src/app/features/department/department.model';
import { ChargingStationLocationService } from '../../services/charging-station-location.service';
import { LoadingSpinnerComponent } from '../../../core/loading-spinner/loading-spinner.component';
import { FormatAddressPipe } from '../../pipes/format-address.pipe';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatBadgeModule } from '@angular/material/badge';
import { MatListModule } from '@angular/material/list';

@Component({
  selector: 'adm-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatBadgeModule,
    MatListModule,
    CustomDropDownArrowDirective,
    LoadingSpinnerComponent,
    FormatAddressPipe,
  ],
})
export class LocationSelectorComponent implements OnInit {
  protected changeDetectorRef = inject(ChangeDetectorRef);
  dialogRef = inject<MatDialogRef<LocationSelectorComponent>>(MatDialogRef);
  data = inject<{
    selectedLocations: ChargingStationLocation[];
    organizationId: number;
    departmentId?: number;
  }>(MAT_DIALOG_DATA);
  private locationService = inject(ChargingStationLocationService);
  private sanitizer = inject(DomSanitizer);
  private departmentService = inject(DepartmentService);

  public locationItems: ChargingStationLocation[] = [];
  selectedLocations: ChargingStationLocation[] = [];
  @ViewChild('locations') locations: any = [];
  locationCheckboxes = new FormControl();
  public filterModel: FormGroup = this.initForm();
  public loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public departments: Department[] = [];
  public departmentSearchEvent: DepartmentSearchEvent = {} as DepartmentSearchEvent;
  public locationSearchEvent: ChargingStationLocationSearchEvent = {} as ChargingStationLocationSearchEvent;

  get selectedDepartmentId() {
    return this.filterModel.get('selectedDepartmentId');
  }

  ngOnInit(): void {
    this.loading.next(true);

    this.getDepartments();

    this.selectedLocations = this.data?.selectedLocations.filter(l => l.organizationId === this.data.organizationId);
    if (this.data.departmentId) {
      this.selectedDepartmentId?.disable();
    }
    this.locationCheckboxes.valueChanges.subscribe((values: ChargingStationLocation[]) => {
      values.forEach(value => {
        value.checked = true;
        if (this.selectedLocations.findIndex(v => v.identifier === value.identifier) === -1) {
          this.selectedLocations.push(value);
        }
      });
    });

    this.filterModel.valueChanges
      .pipe(
        startWith(''), // let one blank through to search for all
        debounceTime(100),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(val => {
        let query: string | undefined;
        let selectedDepartmentId = this.data.departmentId;
        if (val) {
          query = val.query ?? undefined;
          selectedDepartmentId = val.selectedDepartmentId;
        }

        this.locationSearchEvent.query = query;
        this.locationSearchEvent.departmentId = selectedDepartmentId;
        this.locationSearchEvent.organizationId = this.data.organizationId;

        this.loading.next(true);

        this.locationService.getChargingStationLocations(this.locationSearchEvent).subscribe({
          next: locs => {
            this.populateAddress(locs);
            this.locationItems = locs.items;

            this.selectedLocations.forEach(loc => {
              const currentLocation = this.locationItems.find(id => id.identifier === loc.identifier);
              if (currentLocation) {
                currentLocation.checked = true;
              }
            });
            this.loading.next(false);

            //due to ExpressionChangedAfterItHasBeenCheckedError when populating matBadge counter
            this.changeDetectorRef.detectChanges();
          },
          error: () => {
            this.loading.next(false);
          },
        });
        //due to ExpressionChangedAfterItHasBeenCheckedError when populating matBadge counter
        this.changeDetectorRef.detectChanges();
      });
  }

  public locationOptionWasClicked(locationIdentifier: string) {
    const indexOption = (this.locationCheckboxes.value as ChargingStationLocation[]).findIndex(v => v.identifier === locationIdentifier);
    const indexLocation = this.selectedLocations.findIndex(v => v.identifier === locationIdentifier);

    if (indexOption === -1) {
      this.selectedLocations.splice(indexLocation, 1);
    }

    if (this.selectedLocations.length === 0) {
      this.selectedDepartmentId?.setValue(null);
    }
  }

  public resetLocations() {
    this.selectedLocations = [];
    this.locationCheckboxes.setValue(this.selectedLocations);
    this.selectedDepartmentId?.setValue(null);
  }

  public loadSelectedLocations() {
    if (this.selectedLocations.length > 0) {
      this.locationItems = this.selectedLocations;
    }
  }

  saveAndClose() {
    this.dialogRef.close(this.selectedLocations);
  }

  close() {
    this.dialogRef.close(false);
  }

  private async getDepartments() {
    this.departmentSearchEvent.organizationId = this.data.organizationId;
    const pagedDepartments = await firstValueFrom(this.departmentService.getDepartments(this.departmentSearchEvent));
    this.departments = pagedDepartments.items;
    if (this.data.departmentId) {
      this.departments = this.departments.filter(d => d.id === this.data.departmentId);
    }
  }

  private populateAddress(pagedLocation: PagedResultV2<ChargingStationLocation>) {
    pagedLocation.items.forEach(location => {
      location.address = new Address(location.city, location.postalCode, location.road, location.number, location.letter);
    });
  }

  private initForm() {
    return new FormGroup({
      query: new FormControl(''),
      selectedDepartmentId: new FormControl<number | null>(this.data.departmentId ?? null),
    });
  }
}
